import type { StringIndexedObject } from '@kanda-libs/ks-component-ts';
import { actions } from '@kanda-libs/ks-frontend-services';
import { useAppDispatch } from 'components/App';
import { DEV_CIDS } from 'config';
import { useCurrentCompany } from 'hooks';
import useApiError from 'hooks/useApiError';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectors } from 'store';
import { AMENDED_STEP } from './CreateJob-constants';
import { formatJobPayload } from './CreateJob-functions';
import useCreateJobContextValue from './CreateJob-useCreateJobContextValue';

export interface SubmitChangesHook {
  handleSubmit: (formData: StringIndexedObject) => void;
  isSubmitting: boolean;
}

/**
 * Handles job chnages submit
 */
const useSubmitChanges = (): SubmitChangesHook => {
  const dispatch = useAppDispatch();

  const isSubmitting = useSelector(selectors.job.getIsSubmitting);

  const { data, handleContinue } = useCreateJobContextValue();
  const { company } = useCurrentCompany();

  const onError = useApiError(
    'Error updating job at this time. Please try again later',
  );

  const onStepError = useApiError(
    'Error updating job step at this time. Please try again later',
  );

  /**
   * Handles submit job
   */
  const handleSubmit = useCallback(
    (formData: StringIndexedObject) => {
      if (!company) return;
      const cid = company?.cid;
      const id = formData?.job?.id;
      if (!id) return;
      const notes = data?.job?.notes?.[0] || '';
      const payload = formatJobPayload(formData, notes, company);
      dispatch(
        actions.putJob({
          body: payload,
          params: {
            id,
          },
          useDevHeader: cid ? DEV_CIDS.includes(cid) : false,
          chainedRequest: true,
          onError,
          onSuccess: (updatedJob) => {
            dispatch(
              actions.stepJob({
                body: AMENDED_STEP,
                params: {
                  id,
                },
                onError: onStepError,
                onSuccess: () => {
                  handleContinue({
                    ...formData,
                    job: updatedJob,
                  });
                },
              }),
            );
          },
        }),
      );
    },
    [data, handleContinue, dispatch, company, onError, onStepError],
  );

  return {
    handleSubmit,
    isSubmitting,
  };
};

export default useSubmitChanges;
